import React, {useEffect, useState} from "react";
import { RadioGroup, ReversedRadioButton } from "react-radio-buttons";
import {Controller, useForm} from "react-hook-form";
import StepHeader from "./stepParts/StepHeader";
import {
  addOfficeIdParamToLink,
  CALCULATOR_TYPES,
  currencyToNumber, getCalculatorType, numberToCurrency,
  rewriteRadioOptionsClasses,
  stepStates,
} from "../../services/helpers";
import Input from "../core/Input";
import { gtm } from "../../services/gtm";
import { useSelector } from "react-redux";
import {getStep4HeaderSummary, getStepParams} from "../../services/stepHelpers";
import HomeMortgageStart from "./stepParts/HomeMortgageStart";
import NextButton from "./stepParts/NextButton";
import {FormattedMessage, useIntl} from "react-intl";

function Step4({
    stepNumber,
    state,
    openStep,
    loading,
    onStepSubmit,
    shouldReset,
    setResetForms,
}) {
    const intl = useIntl();
    const { HasPartner, Woning } = useSelector((state) => state.calculator);
    const [HasHouse, setHasHouse] = useState();
    const [stepHeaderSummary, setStepHeaderSummary] = useState();
    const [params] = useState(getStepParams());
    const getDefaultValues = (params) => {
      return {
        OwnResources: numberToCurrency(params?.OwnResources, true),
        HomeEquity: numberToCurrency(params?.HomeEquity, true),
        HomeEquityCosts: numberToCurrency(params?.HomeEquityCosts, true),
        HomeMortgageAmount: numberToCurrency(params?.HomeMortgageAmount, true),
        HomeMortgageStart: params?.HomeMortgageStart,
        HomeMortgageStartNotKnown: params?.HomeMortgageStartNotKnown?.toString(),
        HomeMortgageStartBefore2013: params?.HomeMortgageStartBefore2013?.toString(),
        HomeMortgageIsBeingPaidOff: params?.HomeMortgageIsBeingPaidOff?.toString(),
        HomeMortgageOtherPartAmount: numberToCurrency(params?.HomeMortgageOtherPartAmount, true),
        WantsToSkipMortgageQuestions: (params?.WantsToSkipMortgageQuestions) ? params?.WantsToSkipMortgageQuestions?.toString() : "true",
      };
    }
    const {
        register,
        handleSubmit,
        formState,
        errors,
        control,
        setValue,
        watch,
        getValues,
        reset,
    } = useForm({
        mode: "onChange",
        defaultValues: getDefaultValues(params),
    });

    const watchHomeMortgageIsBeingPaidOff = watch("HomeMortgageIsBeingPaidOff");
    const watchWantsToSkipMortgageQuestions = watch("WantsToSkipMortgageQuestions");
    const watchHomeMortgageOtherPartAmount = watch("HomeMortgageOtherPartAmount");
    const watchHomeEquity = watch("HomeEquity");

    const watchHomeMortgageStartNotKnown = watch("HomeMortgageStartNotKnown");

    useEffect(() => {
        if (currencyToNumber(watchHomeEquity) > 0) {
            setValue(
                "HomeEquityCosts",
            numberToCurrency(Math.round(
                    (currencyToNumber(watchHomeEquity) / 100) * 2
                ), true),
                { shouldDirty: true }
            );
        }
    }, [watchHomeEquity, setValue]);

    useEffect(() => {
        params &&
            setStepHeaderSummary(
                getStep4HeaderSummary(
                    params?.HomeEquity,
                    params?.OwnResources,
                    params?.HomeEquityCosts,
                    params?.HomeMortgageAmount,
                    params.Woning,
      false,
                    intl
                )
            );
    }, [setStepHeaderSummary, params, intl]);

    useEffect(() => {
        if (shouldReset) {
            reset(getDefaultValues({}));
            setStepHeaderSummary("");
            setResetForms(false);
        }
    }, [shouldReset, setResetForms, reset]);

    const watchValues = watch(["HomeEquity", "OwnResources"]);
    // fix merge master with HAM-848!
    const MortgageLessThenSurplus =
        Woning > 0
            ? currencyToNumber(watchValues.HomeEquity) +
                  currencyToNumber(watchValues.OwnResources) >
              Woning
            : false;
    const isFormValid =
        formState.isValid && HasHouse && !MortgageLessThenSurplus;

    useEffect(() => {
        setHasHouse(params?.HasHouse ? params?.HasHouse.toString() : "false");
        //setWantsToSkipMortgageQuestions("false");
    }, [setHasHouse, params]);

    rewriteRadioOptionsClasses("already-own-a-house", ["yes", "no"]);

    const onHasHouseChange = (value) => setHasHouse(value);

    const onSubmit = (data) => {
        data.HasHouse = HasHouse;
        data.HomeEquity = currencyToNumber(data.HomeEquity);
        data.HomeEquityCosts = currencyToNumber(data.HomeEquityCosts);
        data.HomeMortgageAmount = currencyToNumber(data.HomeMortgageAmount);
        data.HomeMortgageIsBeingPaidOff = data.HomeMortgageIsBeingPaidOff === "true";
        data.HomeMortgageStartNotKnown = data.HomeMortgageStartNotKnown === "true";
        data.HomeMortgageStartBefore2013 = data.HomeMortgageStartBefore2013 === "true";
        data.WantsToSkipMortgageQuestions = data.WantsToSkipMortgageQuestions === "true"
        data.HomeMortgageOtherPartAmount = currencyToNumber(
            data.HomeMortgageOtherPartAmount
        );
        data.OwnResources = currencyToNumber(data.OwnResources);

        setStepHeaderSummary(
            getStep4HeaderSummary(
                data.HomeEquity,
                data.OwnResources,
                data.HomeEquityCosts,
                data.HomeMortgageAmount,
                Woning,
            false,
            intl
            )
        );

        const tagManagerArgs = {
            gtmId: "GTM-T78CX5",
            dataLayer: {
                event: "gtm.calculators",
                eventCategory: "Calculator",
                eventAction: "Inleg eigen geld",
                eventLabel: "koophuis-" + HasHouse,
            },
        };

        gtm(tagManagerArgs);

        onStepSubmit(data, stepNumber);
    };

    return (
        <div
            className={`ct-step ${
                state === stepStates.inactive && "ct-step__inactive"
            }
                                 ${
                                     state === stepStates.filled &&
                                     "ct-step__filled"
                                 }
                                 ${
                                     state === stepStates.closed &&
                                     "ct-step__closed"
                                 }`}
        >
            <StepHeader
                title={intl.formatMessage({id: "step4.headerTitle", defaultMessage: "Inleg eigen geld"})}
                stepSummary={stepHeaderSummary}
                stepNumber={stepNumber}
                stepState={state}
                openStep={openStep}
                onClose={handleSubmit(onSubmit)}
            />

            <div
                className={`ct-step__body ${
                    state === stepStates.active ? "ct-step__body-opened" : null
                }`}
            >
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {Woning > 0 &&
                        <div>
                            <div className="ct-step__body-primary-question">
                              <FormattedMessage
                              id="step4.ownResources"
                              defaultMessage="Hoeveel eigen geld {salutation}  inbrengen?"
                              values={{
                                salutation: HasPartner ?
                                intl.formatMessage({ id: 'step4.ownResources.hasPartner.yes', defaultMessage: 'willen jullie' }) :
                                intl.formatMessage({ id: 'step4.ownResources.hasPartner.no', defaultMessage: 'wil je' })
                              }}
                              />
                            </div>

                            <div className="ct-step__body-tip">
                              <FormattedMessage id={"step4.ownResources.tip"} defaultMessage={"Bij de aankoop van een huis moet je rekening houden met een deel eigen geld. Dit is gemiddeld ongeveer 6% van de woningwaarde. Vul hier in hoeveel eigen geld je kunt of wilt inleggen."} />
                            </div>

                            <Input
                                placeholder="€"
                                type="currency"
                                register={register}
                                validationRules={{ required: false }}
                                error={errors.OwnResources}
                                name="OwnResources"
                                errorTitle={intl.formatMessage({ id: 'step4.ownResources.errorTitle', defaultMessage: 'eigen geld' })}
                            />
                          </div>
                    }
                    <div className="ct-step__body-secondary-questions-block">
                        <div className="ct-step__body-secondary-question">
                            <FormattedMessage
                            id="step4.hasHouse"
                            defaultMessage="{salutation} al een koophuis?*"
                            values={{
                              salutation: HasPartner ?
                              intl.formatMessage({ id: 'step4.hasHouse.hasPartner.yes', defaultMessage: 'Hebben jullie' }) :
                              intl.formatMessage({ id: 'step4.hasHouse.hasPartner.no', defaultMessage: 'Heb je' })
                            }}
                            />
                        </div>

                        <div className="ct-step__body-tip">
                          <FormattedMessage id={"step4.hasHouse.tip"} defaultMessage={"Heb je al een koophuis? Dan kan je de overwaarde van je woning gebruiken voor de aanschaf van je nieuwe woning."} />
                        </div>

                        <RadioGroup
                            onChange={onHasHouseChange}
                            value={HasHouse}
                            horizontal
                            className="ct-step__radio-group already-own-a-house"
                        >
                            <ReversedRadioButton
                                value="true"
                                rootColor="#000000"
                                pointColor="#000000"
                                iconInnerSize={18}
                                iconSize={18}
                            >
                              <FormattedMessage id={"step4.hasHouse.yes"} defaultMessage={"Ja"} />
                            </ReversedRadioButton>

                            <ReversedRadioButton
                                value="false"
                                rootColor="#000000"
                                pointColor="#000000"
                                iconInnerSize={18}
                                iconSize={18}
                            >
                              <FormattedMessage id={"step4.hasHouse.no"} defaultMessage={"Nee"} />
                            </ReversedRadioButton>
                        </RadioGroup>

                        {HasHouse === "true" &&
                        <div>
                          {!MortgageLessThenSurplus && HasHouse === "true" && getCalculatorType() !== CALCULATOR_TYPES.watWordenMijnMaandLasten && (
                            <div className="ct-input_info-msg ct-input_error-msg--no-margin-top margin-bottom-20">
                              <FormattedMessage id={'step4.mortgageLessThenSurplus'}
                                                defaultMessage={'Je huidige hypotheek heeft invloed op de hoogte van je nieuwe maximale hypotheek. Voor een goede indicatie van de nieuwe maximale hypotheek stellen we hieronder wat vragen over je huidige hypotheek. Heb je deze gegevens niet bij de hand of wil je nu alleen een snelle berekening doen dan kan je dit gedeelte overslaan. Let er dan wel op dat je maximale hypotheek lager kan uitvallen dan de uitslag van deze berekening. Onze adviseurs kunnen een gedetailleerde berekening voor je maken bij {appointmentLink} bij je in de buurt.'}
                                                values={{
                                                  appointmentLink: <a
                                                  href={addOfficeIdParamToLink(`https://www.hypotheker.nl/afspraak-maken-hypotheekadviseur/`)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  >
                                                    {intl.formatMessage({ id: 'step4.mortgageLessThenSurplus.appointmentLink', defaultMessage: 'een vestiging' })}
                                                  </a>
                                                }}
                              />
                            </div>
                          )
                          }
                            <div className="ct-step__body-tip">
                              <FormattedMessage
                              id="step4.skipHasHouseQuestions"
                              defaultMessage="Wil je de vragen over {salutation} huidige hypotheek overslaan?*"
                              values={{
                                salutation: HasPartner ?
                                intl.formatMessage({ id: 'step4.skipHasHouseQuestions.hasPartner.yes', defaultMessage: 'jullie' }) :
                                intl.formatMessage({ id: 'step4.skipHasHouseQuestions.hasPartner.no', defaultMessage: 'je' })
                              }}
                              />
                            </div>

                          <Controller
                          as={
                            <RadioGroup
                                horizontal
                                className="ct-step__radio-group already-own-a-house"
                            >
                              <ReversedRadioButton
                                  value="true"
                                  rootColor="#000000"
                                  pointColor="#000000"
                                  iconInnerSize={18}
                                  iconSize={18}
                              >
                                <FormattedMessage id={"step4.skipHasHouseQuestions.yes"} defaultMessage={"Ja"} />

                              </ReversedRadioButton>

                              <ReversedRadioButton
                                  value="false"
                                  rootColor="#000000"
                                  pointColor="#000000"
                                  iconInnerSize={18}
                                  iconSize={18}
                              >
                                <FormattedMessage id={"step4.skipHasHouseQuestions.no"} defaultMessage={"Nee"} />

                              </ReversedRadioButton>
                            </RadioGroup>
                          }
                          rules={{ required: true }}
                          name="WantsToSkipMortgageQuestions"
                          control={control}
                          />
                        </div>
                        }
                    </div>

                    {HasHouse === "true" && watchWantsToSkipMortgageQuestions === "false" &&
                        <div className={"ct-step__body-secondary-subquestions"}>
                            <div>
                                <div>
                                    <div className="ct-step__body-subquestion">

                                      <FormattedMessage
                                      id="step4.HomeEquity"
                                      defaultMessage="Wat is de verkoopprijs van {salutation} huidige koophuis?*"
                                      values={{
                                        salutation: HasPartner ?
                                        intl.formatMessage({ id: 'step4.HomeEquity.hasPartner.yes', defaultMessage: 'jullie' }) :
                                        intl.formatMessage({ id: 'step4.HomeEquity.hasPartner.no', defaultMessage: 'je' })
                                      }}
                                      />
                                    </div>

                                    <div className="ct-step__body-tip">
                                      <FormattedMessage id={"step4.HomeEquity.tip"} defaultMessage={"Als je deze niet weet, kun je hier de vraagprijs van je woning noteren of een schatting maken."} />
                                    </div>

                                    <Input
                                        placeholder="€"
                                        type="currency"
                                        register={register}
                                        validationRules={{ required: true, min: 1.0 }}
                                        name="HomeEquity"
                                        errorTitle={intl.formatMessage({ id: 'step4.HomeEquity.errorTitle', defaultMessage: 'vraagprijs' })}
                                    />
                                </div>
                                <div>
                                    <div className="ct-step__body-subquestion">
                                      <FormattedMessage id={"step4.HomeEquityCosts"} defaultMessage={"Wat zijn de verkoopkosten?*"} />
                                    </div>

                                    <div className="ct-step__body-tip">
                                      <FormattedMessage id={"step4.HomeEquityCosts.tip"} defaultMessage={"Bij het verkopen van je huis heb je te maken met o.a. de kosten van de makelaar. Dit is vaak rond de 2% van de verkooprijs."} />
                                    </div>

                                    <Input
                                        placeholder="€"
                                        type="currency"
                                        register={register}
                                        validationRules={{ required: true }}
                                        name="HomeEquityCosts"
                                        errorTitle={intl.formatMessage({ id: 'step4.HomeEquityCosts.errorTitle', defaultMessage: 'verkoop kosten' })}
                                    />
                                </div>
                                <div>
                                    <div className="ct-step__body-subquestion">
                                      <FormattedMessage id={"step4.homeMortgageAmount"} defaultMessage={"Wat is de actuele hoogte van {salutation} huidige hypotheek?*"} values={{
                                        salutation: HasPartner ?
                                        intl.formatMessage({ id: 'step4.homeMortgageAmount.hasPartner.yes', defaultMessage: 'jullie' }) :
                                        intl.formatMessage({ id: 'step4.HomeMortgageAmount.hasPartner.no', defaultMessage: 'je' })
                                      }} />
                                    </div>

                                    <Input
                                        placeholder="€"
                                        type="currency"
                                        register={register}
                                        validationRules={{ required: true }}
                                        name="HomeMortgageAmount"
                                        errorTitle={intl.formatMessage({ id: 'step4.homeMortgageAmount.errorTitle', defaultMessage: 'huidige hypotheek' })}
                                    />
                                </div>
                                <div>
                                    <div className="ct-step__body-subquestion">
                                      <FormattedMessage id={"step4.homeMortgageType"} defaultMessage={"Is deze hypotheek een volledig annuïtair en/of lineair?*"} />
                                    </div>

                                    <Controller
                                        as={
                                            <RadioGroup
                                                horizontal
                                                className="ct-step__radio-group"
                                            >
                                                <ReversedRadioButton
                                                    value="true"
                                                    rootColor="#000000"
                                                    pointColor="#000000"
                                                    iconInnerSize={18}
                                                    iconSize={18}
                                                >

                                                  <FormattedMessage id={"step4.homeMortgageType.yes"} defaultMessage={"Ja"} />
                                                </ReversedRadioButton>

                                                <ReversedRadioButton
                                                    value="false"
                                                    rootColor="#000000"
                                                    pointColor="#000000"
                                                    iconInnerSize={18}
                                                    iconSize={18}
                                                >

                                                  <FormattedMessage id={"step4.homeMortgageType.no"} defaultMessage={"Nee"} />
                                                </ReversedRadioButton>
                                            </RadioGroup>
                                        }
                                        rules={{ required: true }}
                                        defaultValue={"true"}
                                        name="HomeMortgageIsBeingPaidOff"
                                        control={control}
                                    />
                                </div>

                                {watchHomeMortgageIsBeingPaidOff === "true" && (
                                    <HomeMortgageStart
                                        register={register}
                                        control={control}
                                        watchHomeMortgageStartNotKnown={
                                            watchHomeMortgageStartNotKnown
                                        }
                                        paidOff={
                                            watchHomeMortgageIsBeingPaidOff ===
                                            "true"
                                        }
                                    ></HomeMortgageStart>
                                )}
                                {watchHomeMortgageIsBeingPaidOff ===
                                    "false" && (
                                    <div>
                                        <div>
                                            <div className="ct-step__body-subquestion">
                                              <FormattedMessage id={"step4.homeMortgageOtherPartAmount"} defaultMessage={"Wat is de actuele hoogte van het (deel) van de hypotheek dat een andere hypotheekvorm heeft?*"} />
                                            </div>

                                            <Input
                                                placeholder="€"
                                                type="currency"
                                                register={register}
                                                validationRules={{
                                                    required: true,
                                                }}
                                                name="HomeMortgageOtherPartAmount"
                                                errorTitle={intl.formatMessage({ id: 'step4.homeMortgageOtherPartAmount.errorTitle', defaultMessage: 'huidige hypotheek' })}
                                            />
                                        </div>

                                        {watchHomeMortgageOtherPartAmount &&
                                            currencyToNumber(
                                                watchHomeMortgageOtherPartAmount
                                            ) <
                                                currencyToNumber(
                                                    getValues(
                                                        "HomeMortgageAmount"
                                                    )
                                                ) && (
                                                <HomeMortgageStart
                                                    register={register}
                                                    control={control}
                                                    watchHomeMortgageStartNotKnown={
                                                        watchHomeMortgageStartNotKnown
                                                    }
                                                    paidOff={
                                                        watchHomeMortgageIsBeingPaidOff ===
                                                        "true"
                                                    }
                                                ></HomeMortgageStart>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    }

                  {MortgageLessThenSurplus &&
                  getCalculatorType() !==
                  CALCULATOR_TYPES.watWordenMijnMaandLasten && (
                  <div className="ct-input_error-msg margin-bottom-20">
                  <FormattedMessage id={'step4.housePriceLessThenSurplus'}
                                    defaultMessage={'De vraagprijs van de woning die je op het oog hebt, is lager dan de overwaarde van je huidige woning en de eigen middelen die je wilt inbrengen. In dit geval zal je geen hypotheek nodig hebben. Wil je wel een hypotheek? Maak dan een afspraak bij {appointmentLink} in de buurt voor advies over je persoonlijke situatie.'}
                                    values={{
                                      appointmentLink: <a
                                      href={addOfficeIdParamToLink(`https://www.hypotheker.nl/afspraak-maken-hypotheekadviseur/`)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      >
                                        {intl.formatMessage({ id: 'step4.housePriceLessThenSurplus.appointmentLink', defaultMessage: 'een vestiging' })}
                                      </a>
                                    }}
                  />
                  </div>
                  )}

                    <NextButton
                        loading={loading}
                        isFormValid={isFormValid}
                        formState={formState}
                    ></NextButton>
                </form>
            </div>
        </div>
    );
}

export default Step4;
